import Quill from '@capaj/quill'
import ImageResize from '@looop/quill-image-resize-module-react'
import ImageUploader from '@looop/quill-image-uploader'
import classNames from 'classnames'
import React, { useContext, useRef, useState } from 'react'
import useOnClickOutside from '../../../../app/hooks/useOnClickOutside'

import { fileUploadInTopics } from '../../../../components/utils/file-upload/fileUploadInTopics'
import EditorQuill from '../../../../components/common/quill/EditorQuill'
import { EditElementWrapper } from '../EditElementWrapper'
import { patchElementContent, elementShouldStayInEditingMode } from '../utils'
import ImageFormat from './ImageFormat'
import './TextElement.less'
import {
  TopicElementModelAsEditor,
  TopicModelAsEditor,
  TopicModelAsMember
} from 'front-end/src/graphql/private/PrivateSchemaTypes'
import { Box } from 'front-end/src/components'
import { TopicEditingContext } from '../../../topic-edit-page/TopicEditContext'

Quill.register(ImageFormat, true)
Quill.register('modules/imageUploader', ImageUploader)
Quill.register('modules/imageResize', ImageResize)

interface IState {
  editing: boolean
  html: string
  percentCompleted: number
}

const TextElement = (props: {
  isMultiPage: boolean
  element: TopicElementModelAsEditor
  topic: TopicModelAsMember | TopicModelAsEditor
}) => {
  const { element, isMultiPage, topic } = props
  const content = element.data?.content as string

  const wrapperRef = useRef(null)
  const [state, setState] = useState<IState>({
    editing: elementShouldStayInEditingMode(isMultiPage, content),
    html: content || '',
    percentCompleted: 100
  })

  const { updateElement: updateElementContent } =
    useContext(TopicEditingContext)

  const patchState = (partial: Partial<IState>) => {
    setState({ ...state, ...partial })
  }

  useOnClickOutside(wrapperRef, () => {
    patchState({
      editing: elementShouldStayInEditingMode(isMultiPage, state.html)
    })
  })

  const handleContentUpdate = (elementId: number, content: string): void => {
    patchState({ html: content })

    updateElementContent(elementId, {
      data: {
        content
      }
    })
  }

  const uploadProgress = (percentCompleted) => {
    patchState({ percentCompleted })
  }

  return (
    <Box ref={wrapperRef}>
      <EditElementWrapper {...props}>
        {!state.editing ? (
          <Box
            onClick={() => patchState({ editing: true })}
            className="ql-editor element-multimedia element-mode-view"
            dangerouslySetInnerHTML={{
              __html: state.html as string
            }}
          />
        ) : (
          <div
            className={classNames('TextElement__editor', {
              uploading: state.percentCompleted !== 100
            })}
          >
            <EditorQuill
              value={state.html}
              autofocus
              onKeyDown={(e) => {
                if (e.key === 'Escape') {
                  patchState({
                    editing: elementShouldStayInEditingMode(
                      isMultiPage,
                      state.html
                    )
                  })
                }
              }}
              onUploadProgress={(percentCompleted) => {
                patchState({ percentCompleted })
              }}
              uploadFn={async (fileName, blob) => {
                return fileUploadInTopics({
                  topicId: topic.id,
                  elementType: element.type,
                  elementId: element.id,
                  blob: blob,
                  fileName,
                  documentType: 'image',
                  on: {
                    uploadProgress
                  }
                })
              }}
              onChange={async (quillContent) => {
                const content = await patchElementContent(
                  element,
                  topic,
                  quillContent,
                  uploadProgress
                )

                handleContentUpdate(element.id, content)
              }}
            />
            {state.percentCompleted !== 100 && (
              <div
                style={{ width: `${state.percentCompleted}%` }}
                className="TextElement__loader"
              />
            )}
          </div>
        )}
      </EditElementWrapper>
    </Box>
  )
}

export default TextElement
